import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { renderMarkup } from 'react-render-markup'
import { useNavigate } from 'react-router'
import { Dispatch } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'

import { usePrevious } from '../hooks'
import {
  addNotification as addNotificationAction,
  removeNotification as removeNotificationAction
} from '../notifications/notifications-actions'
import type { NotificationType } from '../notifications/types'
import { killState as killStateAction } from '../rootReducer'
import { TdsModalConfirm as ModalConfirm } from '../shared/TdsModalConfirm'
import { TdsModalWarning as ModalAutoMedical } from '../shared/TdsModalWarning'
import type { GlobalState } from '../types'
import { IdentityDisplay } from './IdentityDisplay'
import { IdentityForm } from './IdentityForm'

type IdentityProps = {
  addNotification(arg: NotificationType): void
  removeNotification(arg: string): void
  claim: {
    lob: string
    dateOfLoss: string
    claim: string
  }
  error?: string[]
  notifications: NotificationType[]
  formValues?: {
    submitFailed?: boolean
    active?: string
    values?: {
      identityName?: string
      identityEmail?: string
      roleInClaim?: string
    }
    syncErrors?: {
      roleInClaim?: string
    }
  },
  killState(): void
}

type LocalStateType = {
  showComponent: boolean,
  displayNotification: boolean,
  disableContinue: boolean
}

const IdentityComponent = (props: IdentityProps) => {
  const {
    addNotification,
    removeNotification,
    claim,
    formValues,
    notifications,
    error,
    killState
  } = props
  const navigate = useNavigate()
  const prevProps = usePrevious<IdentityProps>(props)

  const [state, setState] = useState<LocalStateType>({
    showComponent: false,
    displayNotification: false,
    disableContinue: false
  })

  const handleBrowserButtonClick = useCallback(() => {
    setState({ ...state, showComponent: true })
  }, [state])

  const handleContinue = useCallback((event: React.SyntheticEvent) => {
    event.preventDefault()
    if (!formValues?.values?.roleInClaim) return
    navigate(`${window.travURI}upload`)
  }, [formValues?.values?.roleInClaim, navigate])

  const handleClose = useCallback(() => {
    window.history.pushState('', document.title, document.location.pathname)
    setState({ ...state, showComponent: false })
  }, [state])

  const handleOK = useCallback(() => {
    killState()
    navigate(window.travURI)
  }, [killState, navigate])

  const roleSelect = useCallback((event: FormEvent<HTMLInputElement>) => {
    if (event.currentTarget.value === 'medicalHealth' && claim.lob.toUpperCase() ==='A') {
      setState({
        ...state,
        disableContinue: true,
        displayNotification:true
      })
    }
     else {
      setState({
        ...state,
        disableContinue: false
      })
    }
  }, [claim.lob, state])

  // converted from componentDidMount
  useEffect(() => {
    // only want to run once on mount, when prevProps is undefined
    if (prevProps) return
    document.title = 'File Uploads - Contact Details'

    window.addEventListener('popstate', handleBrowserButtonClick)
    window.history.pushState(null, document.title, document.location.pathname)
  }, [
    claim,
    handleBrowserButtonClick,
    prevProps
  ])

  // converted from componentDidUpdate
  useEffect(() => {
    if (error && !prevProps?.error) {
      addNotification({
        name: 'identityError',
        body: error.join(''),
        severity: 'warning',
        title: 'REQUIRED FIELD'
      })
    }

    if (!error && notifications.length > 0) {
      removeNotification('identityError')
    }

  }, [
    error,
    notifications,
    removeNotification,
    addNotification,
    prevProps?.error
  ])

  // converted from componentWillUnmount
  useEffect(() => {
    return () => {
      if (notifications.length > 0) {
        removeNotification('identityError')
      }
    }
  }, [notifications, removeNotification])

  const roleUpdateValue =
    formValues
    && formValues.values
    && formValues.values.roleInClaim ? formValues.values.roleInClaim : null

  // format to reflect MM/DD/YYYY format
  const formattedDateOfLost = useMemo(() => {
    return !claim.dateOfLoss
    ? ''
    : claim.dateOfLoss.slice(0,2) +
      '/' + claim.dateOfLoss.slice(2,4) + '/' +
      claim.dateOfLoss.slice(-4)
  }, [claim.dateOfLoss])

  const medicalHelp =
    // eslint-disable-next-line max-len
    `<p>Medical providers should not use this process.  Use eBill or, for more information on how to submit medical bills or reports for consideration under Auto Medical Payments or Personal Injury Protection coverages, please contact the Provider Inquiry Center at 877-838-7281 or <a href="mailto:providerinquiry@travelers.com?subject=${'Claim Number ' + claim.claim + ' – DOL ' + formattedDateOfLost + ' – Upload Center Inquiry'}">providerinquiry@travelers.com</a>.</p>`

  if(
    roleUpdateValue === 'medicalHealth'
    && claim.lob.toUpperCase() === 'A'
    && !state.disableContinue
  ){
    setState({ ...state, displayNotification: true, disableContinue: true })
  }

  return (
    <IdentityDisplay>
      <IdentityForm
        handleSubmit={handleContinue}
        roleSelect={roleSelect}
        disabled={state.disableContinue}
      />
      <ModalConfirm handleOK={handleOK} handleClose={handleClose} />
      {
        state.displayNotification ?
        <ModalAutoMedical
          handleOK={() => {setState({ ...state, displayNotification: false }) } }
          label='Medical Provider Disable'>{renderMarkup(medicalHelp)}
        </ModalAutoMedical>
        : null
      }
    </IdentityDisplay>
  )
}


const identityFormSelector = (state: GlobalState) => state.form.identityForm

const getError = createSelector(
  identityFormSelector,
  formValues =>
    formValues
    && formValues.submitFailed
    && formValues.syncErrors ? ['Some items below need your attention.'] : undefined
)

const notificationSelector = (state: GlobalState) => state.notifications.notifications

const getNotifications = createSelector(
  notificationSelector,
  notifications => notifications.filter((n: NotificationType) => n.name === 'identityError')
)

const mapStateToProps = (state: GlobalState) => ({
  formValues: state.form.identityForm,
  error: getError(state),
  claim: state.claim.claimDetails,
  notifications: getNotifications(state)
})

const mapDispatchToProps = (dispatch: Dispatch) => ({
  killState: () => dispatch(killStateAction()),
  removeNotification: (name: string) => dispatch(removeNotificationAction(name)),
  addNotification: (options: NotificationType) => dispatch(addNotificationAction(options))
})

const Identity = connect(mapStateToProps, mapDispatchToProps)(IdentityComponent)

export {
  Identity,
  IdentityComponent // for testing without HOC
}
